import React from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { SlidersHorizontal } from "lucide-react";

import { cn } from "../../utils/cn";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import DataTablePagination from "./DataTablePagination";

export type DataTableProps<TData> = {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading?: boolean;
  isFetching?: boolean;
  onRowClick?: (row: TData) => void;
  /** Toolbar content rendered above the table (search, filters, actions). */
  toolbar?: React.ReactNode;
  emptyState?: React.ReactNode;
  /** Server-side pagination. Omit to hide the footer. */
  pagination?: {
    page: number;
    totalPages: number;
    totalItems?: number;
    onPageChange: (page: number) => void;
  };
  /** Show the column-visibility menu. */
  enableColumnVisibility?: boolean;
  skeletonRows?: number;
  className?: string;
};

/**
 * Shared table shell for every list in the app.
 *
 * Sorting runs client-side over the *current server page* — the APIs paginate
 * server-side and don't accept a sort param, so sorting reorders the rows you
 * can see rather than the whole result set.
 */
function DataTable<TData>({
  columns,
  data,
  isLoading,
  isFetching,
  onRowClick,
  toolbar,
  emptyState,
  pagination,
  enableColumnVisibility = true,
  skeletonRows = 8,
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const hasRows = table.getRowModel().rows.length > 0;

  return (
    <div className={cn("w-full overflow-hidden rounded-xl border bg-background", className)}>
      {(toolbar || enableColumnVisibility) && (
        <div className="flex flex-col gap-3 border-b p-4 lgss:flex-row lgss:items-center lgss:justify-between">
          <div className="flex flex-1 flex-col gap-3 mds:flex-row mds:items-center">
            {toolbar}
          </div>

          {enableColumnVisibility && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 gap-2 bg-background"
                >
                  <SlidersHorizontal className="size-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 font-manrope">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {typeof column.columnDef.meta === "object" &&
                      column.columnDef.meta &&
                      "label" in column.columnDef.meta
                        ? String((column.columnDef.meta as any).label)
                        : column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted/60">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    className="h-11 whitespace-nowrap text-foreground"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className="hover:bg-transparent">
                  {table.getVisibleFlatColumns().map((column, cellIndex) => (
                    <TableCell key={column.id ?? cellIndex} className="py-3.5">
                      <Skeleton className="h-4 w-full max-w-[140px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : hasRows ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    "border-b transition-colors",
                    onRowClick && "cursor-pointer hover:bg-accent/50"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="whitespace-nowrap py-3.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={table.getVisibleFlatColumns().length}
                  className="h-[320px] p-0 text-center"
                >
                  {emptyState}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && hasRows && (
        <DataTablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          onPageChange={pagination.onPageChange}
          isFetching={isFetching}
        />
      )}
    </div>
  );
}

export default DataTable;
